import {SectionView} from '../../ui_canvas_engine/SectionView'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {UIGamePlayerAvatarStatus} from '../../ui_game/ui_game_players/UIGamePlayerAvatarView'
import {UIGameBaseTradeOfferActionsView} from './UIGameBaseTradeOfferActionsView'
import {UIGamePlayerAvatarContainerView} from '../../ui_game/ui_game_popup/UIGamePlayerAvatarContainerView'
import {PIXI} from '@colonist/pixi'
import {TranslationObject, getNestedTranslation} from '@colonist/i18n'
import {UIGameTradeOfferActionsViewMyTurn} from './UIGameTradeOfferActionsViewMyTurn'
import {UIGameTradeOfferActionsViewNotMyTurn} from './UIGameTradeOfferActionsViewNotMyTurn'
import {UIGameTradePlayerArrowInfoView} from '../UIGameTradePlayerArrowInfoView'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {ArrayUtils} from '@colonist/utils'
import {TradeViewSpriteButton} from '../TradeViewSpriteButton'
import {TradeOfferOpponentDisplay, TradePlayerCreatingCounterOfferStatus, TradePlayerResponseStatus, TradeValidator} from '@colonist/game-mechanic-validators'
import {CardEnum, PlayerColor, TradeResponseStatus} from '@colonist/game-states'

export abstract class UIGameTradeOfferViewHalf extends SectionView {
    protected readonly uiGameManager: UIGameManager
    protected readonly stateController: TradeValidator
    protected readonly playerArrowInfo: UIGameTradePlayerArrowInfoView

    constructor(uiGameManager: UIGameManager, x: number, y: number, width: number, height: number, tradeStateController: TradeValidator, arrowTexture: PIXI.Texture, container: PIXI.Container, itemSize: number) {
        super(x, y, width, height, container, true)

        this.uiGameManager = uiGameManager
        this.stateController = tradeStateController

        const buttonSectionSize = UIGameTradeOfferViewHalf.getWidthOfButtonSection(uiGameManager, itemSize)
        const marginBetweenCardsAndButtons = this.height * 0.1
        const playerArrowInfoWidth = this.width - buttonSectionSize - marginBetweenCardsAndButtons

        const partitionEnabled = false
        this.playerArrowInfo = new UIGameTradePlayerArrowInfoView(uiGameManager, 0, 0, playerArrowInfoWidth, height, this.getAvatarLeftPlayerColor(), arrowTexture, this.getCards(), this, true, partitionEnabled)
    }

    static getWidthOfButtonSection(uiGameManager: UIGameManager, itemSize: number): number {
        const numberOfPlayer = uiGameManager.gameState.players.length
        const numberOfButtonsShownOnMyTurn = numberOfPlayer - 1 + 2
        const widthOfButtonsOnMyTurn = numberOfButtonsShownOnMyTurn * itemSize

        const numberOfAvatarsShownWhenNotMyTurn = numberOfPlayer - 2
        const widhtOfAvatarsWhenNotMyTUrn = numberOfAvatarsShownWhenNotMyTurn * itemSize

        const numberOfButtonsWhenNotMyTurn = 3
        const widthOfButtonsWhenNotMyTurn = numberOfButtonsWhenNotMyTurn * itemSize

        const widthOfButtonSectionWhenNotMyTurn = Math.max(widthOfButtonsWhenNotMyTurn, widhtOfAvatarsWhenNotMyTUrn)

        const buttonSectionWidth = uiGameManager.gameController.isMyTurn ? widthOfButtonsOnMyTurn : widthOfButtonSectionWhenNotMyTurn

        return buttonSectionWidth
    }

    protected abstract getAvatarLeftPlayerColor(): PlayerColor
    protected abstract getCards(): CardEnum[]
}

export class UIGameTradeOfferViewReceivingHalf extends UIGameTradeOfferViewHalf {
    protected readonly avatarsContainer?: UIGamePlayerAvatarContainerView

    constructor(uiGameManager: UIGameManager, y: number, width: number, height: number, tradeStateController: TradeValidator, container: PIXI.Container, itemSize: number) {
        const canvas = uiGameManager.canvasController
        const arrowTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade_arrow_green)
        super(uiGameManager, 0, y, width, height, tradeStateController, arrowTexture, container, itemSize)

        if(this.stateController.getOfferComponentsToDisplay(this.uiGameManager.gameController.myColor) != TradeOfferOpponentDisplay.AvatarContainer) return

        const playerColors = this.uiGameManager.gameState.players.map((player) => player.color)
        const playerColorsExceptRecipientAndMe = playerColors.filter((playerColor) => {
            if(playerColor === this.stateController.getRecipientColor(this.uiGameManager.gameController.myColor)) return false
            if(playerColor === this.uiGameManager.gameController.myColor) return false
            if(this.uiGameManager.gameController.isPlayerSpectator && playerColor == this.uiGameManager.gameController.myColor) return false

            return true
        })
        const avatarsContainerWidth = playerColorsExceptRecipientAndMe.length * itemSize
        const avatarsContainerX = this.width - avatarsContainerWidth - canvas.margin
        const avatarsContainerH = itemSize * 0.91
        const avatarsContainerY = (this.height - avatarsContainerH) / 2
        this.avatarsContainer = new UIGamePlayerAvatarContainerView(this.uiGameManager, avatarsContainerX, avatarsContainerY, avatarsContainerWidth, avatarsContainerH, playerColorsExceptRecipientAndMe, this, {removeBg: true})
        this.avatarsContainer.resetAllPlayerAlpha()

        this.onPlayerResponseStatus()

        this.stateController.getPlayersCreatingCounterOffer().forEach((playersCreatingCounterOffer) => {
            this.onPlayerCreatingCounterOffer({player: playersCreatingCounterOffer.player, isCreatingCounterOffer: playersCreatingCounterOffer.isCreatingCounterOffer})
        })
    }

    onPlayerResponseStatus() {
        if(!this.avatarsContainer) return

        for(const playerResponseStatus of this.stateController.getPlayerResponseStates()) {
            const {player, responseStatus} = playerResponseStatus
            const avatarStatus = UIGameTradeOfferViewReceivingHalf.getTradeStatusToAvatarStatus(responseStatus)
            const avatarTooltip = this.getAvatarResponseTooltip(playerResponseStatus)
            this.avatarsContainer.setPlayerStatus(player, avatarStatus)
            this.avatarsContainer.setPlayerTooltip(player, avatarTooltip)
        }
    }

    protected getAvatarLeftPlayerColor(): PlayerColor {
        return this.stateController.getRecipientColor(this.uiGameManager.gameController.myColor)
    }

    protected getCards(): CardEnum[] {
        return this.stateController.getReceivingResources(this.uiGameManager.gameController.myColor)
    }

    private getAvatarResponseTooltip(playerResponseStatus: TradePlayerResponseStatus): TranslationObject[] {
        const {player, responseStatus} = playerResponseStatus
        const playerName = this.uiGameManager.gameController.getPlayerNameWithColor(player)
        const recipientColor = this.stateController.getRecipientColor(this.uiGameManager.gameController.myColor)
        const recipientPlayerName: TranslationObject = recipientColor === PlayerColor.None ? {key: 'strings:colonistCommon.utils.#buffer', options: {value: ''}} : this.uiGameManager.gameController.getPlayerNameWithColor(recipientColor)
        const tooltip = UIGameTradeOfferViewReceivingHalf.getTradeStatusToTooltip(responseStatus)

        return [getNestedTranslation({
            key: tooltip.key,
        }, {
            playerUsername: playerName,
            recipientPlayerName: recipientPlayerName,
        })]
    }

    onPlayerControllerState() {
        void this.playerArrowInfo.refreshAvatarLeftIcon()
        void this.avatarsContainer?.refreshPlayerIcons()
    }

    onPlayerCreatingCounterOffer(tradePlayerCreatingCounterOffer: TradePlayerCreatingCounterOfferStatus) {
        const {player, isCreatingCounterOffer} = tradePlayerCreatingCounterOffer

        isCreatingCounterOffer ? this.avatarsContainer?.setPlayerCreatingCounterOffer(player) : this.avatarsContainer?.removePlayerCreatingCounterOffer(player)
    }

    private static getTradeStatusToAvatarStatus(tradeStatus: TradeResponseStatus): UIGamePlayerAvatarStatus {
        switch(tradeStatus) {
            case TradeResponseStatus.Accepted: return UIGamePlayerAvatarStatus.Accepted
            case TradeResponseStatus.Rejected: return UIGamePlayerAvatarStatus.Rejected
            case TradeResponseStatus.PendingResponse: return UIGamePlayerAvatarStatus.Pending
            case TradeResponseStatus.Embargoed: return UIGamePlayerAvatarStatus.Embargoed
        }
    }

    private static getTradeStatusToTooltip(tradeStatus: TradeResponseStatus): TranslationObject {
        switch(tradeStatus) {
            case TradeResponseStatus.Accepted: return {key: 'strings:game.tooltips.tradeOfferPlayerAccepted'}
            case TradeResponseStatus.Rejected: return {key: 'strings:game.tooltips.tradeOfferPlayerRejected'}
            case TradeResponseStatus.PendingResponse: return {key: 'strings:game.tooltips.tradeOfferPlayerPending'}
            case TradeResponseStatus.Embargoed: return {key: 'strings:game.tooltips.tradeOfferPlayerEmbargoed'}
        }
    }
}

export class UIGameTradeOfferViewGivingHalf extends UIGameTradeOfferViewHalf {
    protected readonly actions: UIGameBaseTradeOfferActionsView

    constructor(uiGameManager: UIGameManager, y: number, width: number, height: number, tradeStateController: TradeValidator, container: PIXI.Container, itemSize: number) {
        const arrowTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade_arrow_red)
        super(uiGameManager, 0, y, width, height, tradeStateController, arrowTexture, container, itemSize)

        const scaleRatio = (UIUtilsEjsVariables.isUserOnMobileApp && this.uiGameManager.gameState.players.length > 6) ? 0.75 : 1
        const actionsOffset = this.height * 0.5 - itemSize * 0.5
        const actionY = this.height - itemSize * scaleRatio - actionsOffset
        const isMyTurn = this.uiGameManager.gameController.isMyTurn
        this.actions = isMyTurn ? new UIGameTradeOfferActionsViewMyTurn(this.uiGameManager, this, 0, actionY, itemSize * scaleRatio, this.stateController) : new UIGameTradeOfferActionsViewNotMyTurn(this.uiGameManager, this, 0, actionsOffset, itemSize, this.stateController)
        this.actions.x = this.width - this.actions.width - actionsOffset

        this.fadeOutCardsCardsIfIDontHaveThem()
    }

    rejectTrade() {
        this.actions.rejectButtonAction?.()
    }

    onPlayerResponseStatus() {
        this.actions.onPlayerResponseStatus()
    }

    onPlayerActionState() {
        this.actions.onPlayerActionState?.()
    }

    onPlayerControllerState() {
        void this.playerArrowInfo.refreshAvatarLeftIcon()
    }

    onPlayerCreatingCounterOffer(tradePlayerCreatingCounterOffer: TradePlayerCreatingCounterOfferStatus) {
        this.actions.onPlayerCreatingCounterOffer?.(tradePlayerCreatingCounterOffer)
    }

    protected getAvatarLeftPlayerColor(): PlayerColor {
        return this.uiGameManager.gameController.myColor
    }

    protected getCards(): CardEnum[] {
        return this.stateController.getGivingResources(this.uiGameManager.gameController.myColor)
    }

    private fadeOutCardsCardsIfIDontHaveThem() {
        if(this.uiGameManager.gameController.isPlayerSpectator) return
        const cardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor).sort()
        for(const cardView of this.playerArrowInfo.cardContainer.cardInventory.cardViews) {
            if(cardView.cardData.cardEnum === CardEnum.AnyResource) continue

            const hasCardInHand = cardsInHand.includes(cardView.cardData.cardEnum)
            ArrayUtils.removeElementFromArray(cardsInHand, cardView.cardData.cardEnum)

            if(hasCardInHand) continue
            cardView.fadeOutCard()
        }
    }

    toggleActionButtonInteractivity(isInteractive: boolean) {
        this.actions.children.forEach((actionButton) => {
            if(actionButton instanceof TradeViewSpriteButton) actionButton.interactive = isInteractive
        })
    }
}
